import { useCallback, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import getNotifications from '@cartwhl/common/api/getNotifications';
import * as notificationStore from '@cartwhl/common/stores/notificationStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import type { UserNotification } from '@cartwhl/common/types';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import InfiniteLoadingTrigger from '@cartwhl/ui/atoms/InfiniteLoadingTrigger';
import Text from '@cartwhl/ui/base/TextNew';

import NotificationButton from '../NotificationButton';
import NotificationListItem from './components/NotificationListItem';

const NOTIFICATIONS_DROPDOWN_Z_INDEX = 60;
const NOTIFICATIONS_PAGE_SIZE = 20;

export default function NotificationsMenu() {
  const { newNotificationCount } = useSnapshot(notificationStore.state);
  const { loading: userLoading } = useSnapshot(userStore.state);
  const showIndicator = !userLoading && newNotificationCount > 0;

  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [listScrollRoot, setListScrollRoot] = useState<HTMLUListElement | null>(
    null,
  );
  const nextTokenRef = useRef<string | undefined>(undefined);
  const loadingMoreRef = useRef(false);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    nextTokenRef.current = undefined;
    setHasMore(false);

    const { data, error } = await getNotifications({
      limit: NOTIFICATIONS_PAGE_SIZE,
    });
    setLoading(false);

    if (error || !data) {
      setNotifications([]);
      return;
    }

    setNotifications(data.records);
    nextTokenRef.current = data.nextToken;
    setHasMore(Boolean(data.nextToken));
  }, []);

  const loadMore = useCallback(async () => {
    const token = nextTokenRef.current;
    if (!token || loadingMoreRef.current || loading) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);

    const { data, error } = await getNotifications({
      limit: NOTIFICATIONS_PAGE_SIZE,
      nextToken: token,
    });

    loadingMoreRef.current = false;
    setLoadingMore(false);

    if (error || !data) {
      return;
    }

    setNotifications((prev) => {
      const seen = new Set(prev.map((record) => record.notificationID));
      const appended = data.records.filter(
        (record) => !seen.has(record.notificationID),
      );
      return appended.length > 0 ? [...prev, ...appended] : prev;
    });
    nextTokenRef.current = data.nextToken;
    setHasMore(Boolean(data.nextToken));
  }, [loading]);

  const markNotificationRead = useCallback(
    async (notificationID: string) => {
      setNotifications((prev) =>
        prev.map((record) =>
          record.notificationID === notificationID
            ? { ...record, status: 'read' }
            : record,
        ),
      );

      const ok = await notificationStore.setNotificationStatus(
        notificationID,
        'read',
      );
      if (!ok) {
        void loadNotifications();
      }
    },
    [loadNotifications],
  );

  const handleOpenChange = (open: boolean) => {
    if (open) {
      void loadNotifications();
    } else {
      setNotifications([]);
      nextTokenRef.current = undefined;
      setHasMore(false);
    }
  };

  const showInitialLoading = loading && notifications.length === 0;
  const showList = !showInitialLoading && notifications.length > 0;

  return (
    <Dropdown
      side="bottom"
      align="end"
      zIndex={NOTIFICATIONS_DROPDOWN_Z_INDEX}
      onOpenChange={handleOpenChange}
      width={380}
      trigger={<NotificationButton showIndicator={showIndicator} />}
    >
      <div className="flex w-[min(100vw-2rem,380px)] flex-col">
        <div className="border-primary border-b border-solid px-4 py-3">
          <Text token="14/medium" className="text-tertiary">
            Notifications
          </Text>
        </div>
        {showInitialLoading ? (
          <Text token="14" className="text-tertiary px-4 py-6">
            Loading…
          </Text>
        ) : notifications.length === 0 ? (
          <Text token="14" className="text-tertiary px-4 py-6">
            Your notifications will appear here.
          </Text>
        ) : null}
        {showList ? (
          <ul
            ref={setListScrollRoot}
            className="styled-scrollbar max-h-[min(70vh,420px)] overflow-y-auto py-2 pl-2 pr-4"
          >
            {notifications.map((notification) => (
              <NotificationListItem
                key={notification.notificationID}
                notification={notification}
                onMarkRead={markNotificationRead}
              />
            ))}
            {hasMore ? (
              <li className="flex list-none justify-center py-2">
                <InfiniteLoadingTrigger
                  loading={loadingMore}
                  onEnter={() => void loadMore()}
                  root={listScrollRoot}
                  rootMargin="0px 0px 200px 0px"
                />
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </Dropdown>
  );
}
